import { useState, type ReactElement } from 'react'
import { isNotDefined, type UserId } from '@terros/common'
import { Stack, Text } from '@mantine/core'
import { useProfile, useTranslation } from '@front/model'
import { TAlert } from '@components'
import { FlaggedWorkflowLinks } from './FlaggedWorkflowLinks'

export function flaggedQueueNoticeKey(userId: UserId): string {
  return `flagged-conversations-notice-dismissed:${userId}`
}

function readDismissed(userId: UserId): boolean {
  try {
    return window.localStorage.getItem(flaggedQueueNoticeKey(userId)) === 'true'
  } catch {
    return false
  }
}

export function FlaggedQueueNotice(): ReactElement | null {
  const { user } = useProfile()
  if (isNotDefined(user)) return null
  return <UserNotice key={user.userId} userId={user.userId} />
}

function UserNotice({ userId }: { userId: UserId }): ReactElement | null {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() => readDismissed(userId))

  function dismiss(): void {
    try {
      window.localStorage.setItem(flaggedQueueNoticeKey(userId), 'true')
    } catch {}
    setDismissed(true)
  }

  if (dismissed) return null
  return (
    <TAlert title={t('What does Flagged mean?')} withCloseButton onClose={dismiss}>
      <Stack gap='xs'>
        <Text size='sm'>
          {t(
            'Flagged recordings are paused for review because the matched location has an account in a workflow stage configured by your company to flag recordings. This can happen when a recording is matched to the wrong location.'
          )}
        </Text>
        <Text size='sm'>
          {t(
            'Listen to the recording, then choose Discard to permanently delete the conversation and audio, or Fix location to select the correct location and resume processing. Locations in flagged stages cannot be used.'
          )}
        </Text>
        <FlaggedWorkflowLinks />
        {/* TODO(flagged-conversations-help-doc): Link the Intercom help article here when published. */}
      </Stack>
    </TAlert>
  )
}
